/**
 * FEATURE FLAGS — everything we expect to argue about after real users.
 *
 * Three different kinds of tunable, kept deliberately separate:
 *
 *   features.ts  — what is SHOWN and whether a capability exists   (this file)
 *   ruleset.ts   — how behaviour SCORES (XP, ranks, titles)        (versioned)
 *   selectors.ts — the shape the UI reads                          (stable API)
 *
 * Flags are cheap to add and expensive to leave lying around. The rule: a flag
 * earns its place if we genuinely don't know the answer yet and a week of real
 * usage would tell us. Anything settled gets inlined and the flag deleted.
 */
export type FeatureFlags = {
  home: {
    /** Goal countdown line. High value for admission candidates (§38's
     *  own "90 days. One goal."), but noise for a casual habit user. */
    showGoalLine: boolean;
    /** Week strip doubles as date navigation and backfill affordance. */
    showWeekStrip: boolean;
    /** Per-row dots: the consistency grid rotated into the list. The main
     *  thing to test — does it motivate, or just add visual noise? */
    showRowHistory: boolean;
    rowHistoryDays: number;
    showTasks: boolean;
    /** Home shows today + overdue only, never the full backlog. */
    maxTasks: number;
    showTodayStudyTime: boolean;
    /** Today's log: what was recorded, with its track breakdown. The evening
     *  half of the daily loop — Home's tasks card is the same idea for
     *  promises, this one is for evidence. */
    showTodayLog: boolean;
    /** The Comeback card. Off is a supported choice, but the default is on:
     *  it is the highest-evidence element on the screen (§48 #3). */
    showComeback: boolean;
    /** Entries shown before the rest collapse into a count. Same reasoning as
     *  `maxTasks`: Home is today, never an archive. */
    maxEntries: number;
    /** Track chips offered when logging, most recently used first. Enough to
     *  cover a student's subjects without becoming a list to read. */
    trackSuggestions: number;
    /** "You kept 87% of 30 days." The proof-of-work line — the one number on
     *  Home that is a fact about the student rather than a score we invented.
     *  Flagged because we don't yet know whether it motivates or judges. */
    showCommitment: boolean;
    /** Window it averages over. A month is long enough that one bad day
     *  doesn't move it, short enough to still describe who they are now. */
    commitmentDays: number;
    /** §6 says progression is visible but secondary. If activation is weak,
     *  try promoting it; if Home feels busy, try hiding it. */
    showProgression: boolean;
  };

  /** The single highest-leverage retention nudge in a habit app, and the
   *  easiest to make annoying. Off by default until we can watch it. */
  streakNudge: {
    enabled: boolean;
    /** Local hour after which "you haven't done anything today" may appear. */
    afterHour: number;
    /** Don't nag someone with a 2-day streak — there's nothing at stake yet. */
    minStreak: number;
  };

  timer: {
    /** I'd argue a basic timer must be free: §40 defines activation as
     *  "completes first habit/study session", and manual-entry-only makes
     *  that first session high-friction. Flagged so it's testable. */
    basicTimerFree: boolean;
    pomodoro: boolean;
    presets: number[];
  };

  capture: {
    /** Ticking a past cell is legitimate ("forgot yesterday") and also
     *  trivial streak fraud. The window is the whole policy. */
    allowBackfill: boolean;
  };

  log: {
    /** Durations offered as one tap. Flagged because the right list is a fact
     *  about how Bangladeshi coaching blocks are actually shaped, not
     *  something to reason out from a desk — a batch that runs 90 minutes
     *  wants a different set than one that runs 50. */
    minutePresets: number[];
  };

  progress: {
    habitGrid: boolean;
    /** The year at a glance, below the chart on Jatra. */
    yearGrid: boolean;
    gridDays: number;
  };
};

export const defaultFeatures: FeatureFlags = {
  home: {
    showGoalLine: true,
    showWeekStrip: true,
    showRowHistory: true,
    rowHistoryDays: 7,
    showTasks: true,
    maxTasks: 4,
    showTodayStudyTime: true,
    showTodayLog: true,
    showComeback: true,
    maxEntries: 5,
    trackSuggestions: 6,
    showCommitment: true,
    commitmentDays: 30,
    showProgression: true,
  },
  streakNudge: {
    enabled: false,
    afterHour: 20,
    minStreak: 3,
  },
  timer: {
    basicTimerFree: true,
    pomodoro: false,
    presets: [25, 50],
  },
  capture: {
    allowBackfill: true,
  },
  log: {
    minutePresets: [15, 30, 45, 60, 90],
  },
  progress: {
    habitGrid: true,
    yearGrid: true,
    gridDays: 30,
  },
};

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

/**
 * Merge overrides onto the defaults — one level of nesting is all the shape
 * has, so this stays trivially readable rather than a generic deep-merge.
 */
export function withFeatures(overrides: DeepPartial<FeatureFlags>): FeatureFlags {
  const merged = { ...defaultFeatures };
  for (const key of Object.keys(overrides) as (keyof FeatureFlags)[]) {
    merged[key] = { ...defaultFeatures[key], ...overrides[key] } as never;
  }
  return merged;
}
